import { Input } from "components";
import PanelSegment from "components/panels/PanelSegment/PanelSegment";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import { ArtistPreview } from "components/PreviewPanel/Company/CompanyPreviewPanel";
import Table from "components/Tables/Table";
import React, { useState } from "react";
import { useCustomQuery, useManualPagination } from "utilities/hookutils";
import { useDebounce } from "utilities/usehooks";

interface Props {
  queryKey: string;
  perPage: number;
  instruction: Partial<server.dto.ListArtistInstruction>;
  renderLastColumn: (artist: server.dto.ArtistSmall) => React.ReactNode;
  onDelete: (artist: server.dto.ArtistSmall) => void;
  onAdd: () => void;
}

export const LinkedArtistsPanel = (props: Props) => {
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const [total, setTotal] = React.useState<number>(0);
  const debouncedSearchQuery = useDebounce(searchQuery);

  const [instruction, setInstruction] = useState<server.dto.ListArtistInstruction>({
    page: 1,
    perPage: props.perPage,
    artistSecurityScope: "limitByUserAccess",
    ...props.instruction,
  });

  React.useEffect(() => {
    setInstruction({
      ...instruction,
      keyword: debouncedSearchQuery,
      page: 1,
    });
  }, [debouncedSearchQuery]);

  const api = useCustomQuery({
    queryFn: () => window.CoreApi.Artist.PaginatedSmall(instruction),
    queryKey: ["artists", instruction, props.queryKey],
    onSuccess: (response) => {
      if (!debouncedSearchQuery) {
        setTotal(response.data.pagination.total);
      }
      pagination.setPagination(response.data.pagination);
    }
  });

  const paginate = (paginationData: Partial<server.dto.PaginationData>) => {
    setInstruction({
      ...instruction,
      keyword: debouncedSearchQuery,
      page: paginationData.page,
      perPage: paginationData.perPage
    });
  };

  const pagination = useManualPagination(instruction.page || 1, props.perPage, paginate);
  const items = api.data?.data?.data || [];
  const displaySearchBar = total > 10;
  const isLoading = api.isLoading;

  const onChange = () => {
    api.refetch();
  }

  return <SectionPanel.Panel
    header={<>
      <PanelSegment.Title title={`${site.currentEnvironment.functionGroups.artist.plural}: ${total}`} />
      {pagination && <PanelSegment.Pagination>{pagination.Component}</PanelSegment.Pagination>}
      <PanelSegment.Button
        label={RESX.Buttons.Add}
        type="secondary"
        onClick={props.onAdd}
      />
    </>}
  >
    {displaySearchBar && <div style={{ paddingLeft: 8 }}>
      <Input
        onChange={(_, { value }) => { setSearchQuery(value); }}
        value={searchQuery}
        icon="search"
        placeholder={RESX.Company.SearchByName}
        fluid
      />
    </div>}
    <Table
      canDelete={() => true}
      onDelete={props.onDelete}
      items={items}
      isLoading={isLoading}
      getKey={artist => artist.artistId.toString()}
      hideHeader
      columns={[{
        key: "name",
        header: "",
        render: (artist) => <ArtistPreview
          companyId={artist.artistId}
          onChange={onChange}
          trigger={<a>{artist.name}</a>}
        />,
        size: "1fr"
      }, {
        key: "preview",
        header: "",
        render: props.renderLastColumn,
      }]}
    />
  </SectionPanel.Panel>
}
